/**
 * Module keys + helpers for website RBAC.
 * Backend keys are source of truth; UI aliases map to them.
 */

export type Role =
  | "SUPER_ADMIN"
  | "ADMIN"
  | "SALES_POINT_ADMIN"
  | "OPERATOR"
  | "MERCHANT"
  | string;

/** UI + backend module keys accepted by useRBAC / ProtectedRoute */
export type Module =
  | "dashboard"
  | "users"
  | "cardholders"
  | "nfc_cards"
  | "transactions"
  | "recharges"
  | "sales_points"
  | "branches"
  | "categories"
  | "products"
  | "product_sales_points"
  | "productList" // alias → product_sales_points
  | "inventory"
  | "sales"
  | "tables"
  | "checkBalance" // alias → nfc_cards
  | "purchases"
  | "reports"
  | "settings"
  | "audit_logs"
  | "auditLogs" // alias → audit_logs
  | "permissions"
  | "recipes"
  | "settlements"
  | "companies";

export interface Permission {
  canRead: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

export interface ModulePermissionDto extends Permission {
  module: string;
  extraActions: string[];
}

/** Frontend module name → backend modules.key */
const MODULE_ALIASES: Record<string, string> = {
  checkBalance: "nfc_cards",
  productList: "product_sales_points",
  auditLogs: "audit_logs",
  nfcCards: "nfc_cards",
  productSalesPoints: "product_sales_points",
  salesPoints: "sales_points",
  audit_logs: "audit_logs",
  product_sales_points: "product_sales_points",
  nfc_cards: "nfc_cards",
  sales_points: "sales_points",
};

export const resolveBackendModule = (module: string): string => {
  return MODULE_ALIASES[module] || module;
};

export const emptyPermission = (): Permission => ({
  canRead: false,
  canCreate: false,
  canUpdate: false,
  canDelete: false,
});

export const findModulePermission = (
  permissions: ModulePermissionDto[] | undefined | null,
  module: string
): ModulePermissionDto | null => {
  if (!permissions?.length) return null;
  const key = resolveBackendModule(module);
  return permissions.find((p) => p.module === key) || null;
};

/**
 * Lookup CRUD from API matrix. If matrix missing (old session), returns null
 * so caller can fall back to legacy ROLE_PERMISSIONS.
 */
export const permissionFromMatrix = (
  permissions: ModulePermissionDto[] | undefined | null,
  module: string,
  action: keyof Permission
): boolean | null => {
  if (!permissions?.length) return null;
  const row = findModulePermission(permissions, module);
  if (!row) return false;
  return Boolean(row[action]);
};

export const hasExtraFromMatrix = (
  permissions: ModulePermissionDto[] | undefined | null,
  module: string,
  extra: string
): boolean => {
  const row = findModulePermission(permissions, module);
  return Boolean(row?.extraActions?.includes(extra));
};

/**
 * @deprecated Offline / migration fallback only. Prefer API `permissions`.
 * Kept so old persisted sessions without matrix still work once.
 */
export type RolePermissions = Record<string, Permission>;

export const ROLE_PERMISSIONS: Record<string, RolePermissions> = {
  SUPER_ADMIN: {
    dashboard: { canRead: true, canCreate: true, canUpdate: true, canDelete: true },
    users: { canRead: true, canCreate: true, canUpdate: true, canDelete: true },
    cardholders: { canRead: true, canCreate: true, canUpdate: true, canDelete: true },
    nfc_cards: { canRead: true, canCreate: true, canUpdate: true, canDelete: true },
    transactions: { canRead: true, canCreate: true, canUpdate: true, canDelete: true },
    recharges: { canRead: true, canCreate: true, canUpdate: true, canDelete: true },
    sales_points: { canRead: true, canCreate: true, canUpdate: true, canDelete: true },
    branches: { canRead: true, canCreate: true, canUpdate: true, canDelete: true },
    categories: { canRead: true, canCreate: true, canUpdate: true, canDelete: true },
    products: { canRead: true, canCreate: true, canUpdate: true, canDelete: true },
    product_sales_points: { canRead: true, canCreate: true, canUpdate: true, canDelete: true },
    productList: { canRead: true, canCreate: true, canUpdate: true, canDelete: true },
    inventory: { canRead: true, canCreate: true, canUpdate: true, canDelete: true },
    sales: { canRead: true, canCreate: true, canUpdate: true, canDelete: true },
    tables: { canRead: true, canCreate: true, canUpdate: true, canDelete: true },
    checkBalance: { canRead: true, canCreate: false, canUpdate: false, canDelete: false },
    purchases: { canRead: true, canCreate: true, canUpdate: true, canDelete: true },
    reports: { canRead: true, canCreate: true, canUpdate: true, canDelete: true },
    settings: { canRead: true, canCreate: true, canUpdate: true, canDelete: true },
    audit_logs: { canRead: true, canCreate: false, canUpdate: false, canDelete: false },
    auditLogs: { canRead: true, canCreate: false, canUpdate: false, canDelete: false },
    permissions: { canRead: true, canCreate: true, canUpdate: true, canDelete: true },
  },
};

export const getPermissions = (
  role: string | null,
  module: Module
): Permission => {
  if (!role) return emptyPermission();
  const rolePerms = ROLE_PERMISSIONS[role];
  if (!rolePerms) return emptyPermission();
  const key = resolveBackendModule(module);
  return (
    rolePerms[module] ||
    rolePerms[key] ||
    emptyPermission()
  );
};

export const hasPermission = (
  role: string | null | undefined,
  module: Module,
  action: keyof Permission
): boolean => {
  if (!role) return false;
  return getPermissions(role, module)[action];
};

export const normalizeRole = (role: string | undefined | null): string | null => {
  if (!role) return null;
  const key = role.toLowerCase().replace(/-/g, "_");
  switch (key) {
    case "super_admin":
    case "superadmin":
      return "SUPER_ADMIN";
    case "admin":
      return "ADMIN";
    case "sales_point_admin":
    case "salespoint_admin":
    case "department_admin":
      return "SALES_POINT_ADMIN";
    case "operator":
    case "user":
    case "cashier":
      return "OPERATOR";
    case "merchant":
      return "MERCHANT";
    default:
      return role.toUpperCase().replace(/[^A-Z0-9_]/g, "_");
  }
};

export const isValidRole = (role: string): boolean => Boolean(normalizeRole(role));
